import { useSyncExternalStore } from "react";
import appointmentReducer from "./slices/appointmentSlice";
import shopReducer from "./slices/shopSlice";
import type { RootState } from "../types";

export interface Action {
  type: string;
  payload?: unknown;
}

// Like Redux Toolkit (immer), state is frozen so an accidental mutation of
// store data throws instead of silently desyncing the UI.
const deepFreeze = <T>(value: T): T => {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach(deepFreeze);
  }
  return value;
};

const reduce = (state: RootState | undefined, action: Action): RootState => ({
  appointments: appointmentReducer(state?.appointments, action),
  shops: shopReducer(state?.shops, action),
});

let state = deepFreeze(reduce(undefined, { type: "@@init" }));
const listeners = new Set<() => void>();

/** The single app store: the same reducers and action types as before, without Redux. */
const store = {
  getState: (): RootState => state,
  dispatch<A extends Action>(action: A): A {
    const next = reduce(state, action);
    if (next.appointments !== state.appointments || next.shops !== state.shops) {
      state = deepFreeze(next);
      listeners.forEach((listener) => listener());
    }
    return action;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export type AppDispatch = typeof store.dispatch;

export const useDispatch = <T = AppDispatch>(): T => store.dispatch as T;

export const useSelector = <T>(selector: (state: RootState) => T): T =>
  useSyncExternalStore(store.subscribe, () => selector(store.getState()));

export default store;
